import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Send, MessageSquare, CheckCircle2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const ContactUs = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [companyAddress, setCompanyAddress] = useState('12 N 2nd Street STE 100,\nRichmond, KY 40475');
  const [companyAddress2, setCompanyAddress2] = useState('');
  const [whatsappLink, setWhatsappLink] = useState('https://wa.me/message/QO7NOBRERE3MO1');

  React.useEffect(() => {
    const loadSettings = async () => {
      try {
        const [addrRes, addr2Res, waRes] = await Promise.all([
          axios.get('/api/settings/COMPANY_ADDRESS').catch(() => ({ data: {} })),
          axios.get('/api/settings/COMPANY_ADDRESS_2').catch(() => ({ data: {} })),
          axios.get('/api/settings/WHATSAPP_LINK').catch(() => ({ data: {} }))
        ]);
        if (addrRes.data?.data?.value) setCompanyAddress(addrRes.data.data.value);
        if (addr2Res.data?.data?.value) setCompanyAddress2(addr2Res.data.data.value);
        if (waRes.data?.data?.value) setWhatsappLink(waRes.data.data.value);
      } catch (_) {}
    };
    loadSettings();
    window.addEventListener('datastore:update', loadSettings);
    return () => window.removeEventListener('datastore:update', loadSettings);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please fill in all required fields (Name, Email, Message)');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post('/api/mail/contact', form);
      if (res.data?.success) {
        toast.success('Your message has been sent to info@averadao.org');
        setSubmitted(true);
        setForm({ name: '', email: '', subject: '', message: '' });
      } else {
        toast.error(res.data?.message || 'Failed to send message');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen text-slate-100 py-12 md:py-18 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0a1628 0%, #0f172a 45%, #172554 100%)' }}
    >
      {/* Background ambient decorative glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-10 left-1/4 w-96 h-96 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(29, 78, 216, 0.15) 0%, transparent 70%)' }}
        />
        <div
          className="absolute bottom-10 right-1/4 w-96 h-96 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)' }}
        />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12 relative z-10">
        {/* Page Header */}
        <div className="text-center space-y-4 max-w-3xl mx-auto">
          <motion.span
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-950/70 border border-blue-500/30 text-blue-300 text-xs md:text-sm font-bold tracking-wider uppercase backdrop-blur-md shadow-inner"
          >
            <Mail className="w-3.5 h-3.5 text-blue-400" />
            Direct Support
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-3xl md:text-5xl font-extrabold text-white tracking-tight"
          >
            Contact AVERADAO Support
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-blue-100/90 text-base md:text-lg leading-relaxed"
          >
            Have questions about refund programs, claims, or protocol verification? Send us a message and our support team will get back to you promptly.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Contact Info Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-4 space-y-6"
          >
            <div
              className="bg-[#0f172a]/85 border border-blue-900/50 rounded-2xl p-6 space-y-6 shadow-2xl backdrop-blur-md"
            >
              <h3 className="text-lg font-bold text-white pb-3 border-b border-blue-900/40 flex items-center gap-2.5">
                <MessageSquare className="w-5 h-5 text-blue-400" />
                Contact Information
              </h3>

              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 rounded-xl bg-[#0a1628]/80 border border-blue-900/40 hover:border-blue-700/50 transition-colors">
                  <div className="p-2.5 rounded-xl bg-blue-600/20 text-blue-400 border border-blue-500/30 shrink-0">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-blue-400 uppercase tracking-wider">Direct Email</h4>
                    <a
                      href="mailto:info@averadao.org"
                      className="text-sm font-semibold text-blue-200 hover:text-white break-all transition-colors"
                    >
                      info@averadao.org
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 rounded-xl bg-[#0a1628]/80 border border-blue-900/40 hover:border-blue-700/50 transition-colors">
                  <div className="p-2.5 rounded-xl bg-blue-600/20 text-blue-400 border border-blue-500/30 shrink-0">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div className="space-y-2">
                    {companyAddress && (
                      <div>
                        <h4 className="text-xs font-bold text-blue-400 uppercase tracking-wider">Administrative Office</h4>
                        <p className="text-sm font-medium text-blue-100/90 leading-snug whitespace-pre-line">
                          {companyAddress}
                        </p>
                      </div>
                    )}
                    {companyAddress2 && (
                      <div className="pt-2 border-t border-blue-900/40">
                        <h4 className="text-xs font-bold text-blue-300 uppercase tracking-wider">Registered Office</h4>
                        <p className="text-sm font-medium text-blue-100/90 leading-snug whitespace-pre-line">
                          {companyAddress2}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 rounded-xl bg-emerald-950/40 border border-emerald-500/30 shadow-md">
                  <div className="p-2.5 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 shrink-0">
                    <span className="text-xl">💬</span>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-emerald-400 uppercase tracking-wider">WhatsApp Support</h4>
                    <a
                      href={whatsappLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-extrabold text-emerald-300 hover:text-emerald-200 transition-colors underline underline-offset-2 flex items-center gap-1 mt-0.5"
                    >
                      Chat on WhatsApp &rarr;
                    </a>
                  </div>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-amber-950/30 border border-amber-500/30 text-amber-200/90 text-xs leading-relaxed">
                <p className="font-semibold text-amber-300 mb-1 flex items-center gap-1.5">
                  <AlertCircle className="w-4 h-4" /> Security Notice
                </p>
                AVERADAO support will never ask for your private keys or seed phrase. All official support messages route to info@averadao.org.
              </div>
            </div>
          </motion.div>

          {/* Interactive Form Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="lg:col-span-8"
          >
            <div className="bg-[#0f172a]/85 border border-blue-900/50 rounded-2xl p-6 sm:p-8 shadow-2xl backdrop-blur-md">
              {submitted ? (
                <div className="py-12 text-center space-y-4">
                  <div className="w-16 h-16 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-full flex items-center justify-center mx-auto shadow-lg shadow-blue-900/30">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Message Sent Successfully!</h3>
                  <p className="text-blue-200/90 text-sm max-w-md mx-auto">
                    Your inquiry has been routed directly to <span className="font-semibold text-blue-300">info@averadao.org</span>. Our support team will review and reply to your email shortly.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-4 px-6 py-2.5 rounded-xl font-bold text-white text-sm transition-all shadow-md cursor-pointer"
                    style={{
                      background: 'linear-gradient(135deg, #1d4ed8, #2563eb)',
                      border: '1px solid rgba(147, 197, 253, 0.4)',
                      boxShadow: '0 4px 15px rgba(29, 78, 216, 0.35)'
                    }}
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="flex items-center justify-between pb-4 border-b border-blue-900/40">
                    <h3 className="text-xl font-bold text-white">Send Us a Direct Message</h3>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-blue-200 mb-2">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="John Doe"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full px-4 py-3 bg-[#0a1628]/90 border border-blue-900/60 rounded-xl outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/25 text-sm font-medium text-white placeholder-blue-300/30 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-blue-200 mb-2">
                        Your Email Address *
                      </label>
                      <input
                        type="email"
                        required
                        placeholder="john@example.com"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="w-full px-4 py-3 bg-[#0a1628]/90 border border-blue-900/60 rounded-xl outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/25 text-sm font-medium text-white placeholder-blue-300/30 transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-blue-200 mb-2">
                      Subject (Optional)
                    </label>
                    <input
                      type="text"
                      placeholder="Inquiry about refund claim / case status"
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      className="w-full px-4 py-3 bg-[#0a1628]/90 border border-blue-900/60 rounded-xl outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/25 text-sm font-medium text-white placeholder-blue-300/30 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-blue-200 mb-2">
                      Your Message *
                    </label>
                    <textarea
                      rows={5}
                      required
                      placeholder="Type your message or inquiry here..."
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="w-full px-4 py-3 bg-[#0a1628]/90 border border-blue-900/60 rounded-xl outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/25 text-sm font-medium text-white placeholder-blue-300/30 transition-all"
                    />
                  </div>

                  <div className="pt-2">
                    <motion.button
                      type="submit"
                      disabled={loading}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl font-extrabold text-white text-base shadow-xl cursor-pointer transition-all disabled:opacity-50"
                      style={{
                        background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 50%, #1e40af 100%)',
                        border: '1px solid rgba(147, 197, 253, 0.4)',
                        boxShadow: '0 8px 25px rgba(29, 78, 216, 0.4)'
                      }}
                    >
                      <Send className="w-4 h-4" />
                      {loading ? 'Sending Message...' : 'Send Message to Support'}
                    </motion.button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
